import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/inherits";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function _callSuper(t, o, e) { return o = _getPrototypeOf(o), _possibleConstructorReturn(t, _isNativeReflectConstruct() ? Reflect.construct(o, e || [], _getPrototypeOf(t).constructor) : o.apply(t, e)); }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
import { Observable } from 'rxjs';
import { delay, retryWhen, share, switchMap } from 'rxjs/operators';
import { APIClient } from '../misc/APIClient';
import decodeJwt from 'jwt-decode';

/**
 * @typedef {Object} MessagingAPI
 * @property {MessageAPI} conversations
 * @property {CallAPI} calls
 * @property {RoomsAPI} rooms
 * @property {Observable<*>} events$
 *
 * @param {Object} apiConfigOpts
 * @returns {MessagingAPI}
 */
export function createMessagingAPI(apiConfigOpts) {
  return {
    conversations: new MessageAPI(apiConfigOpts),
    calls: new CallAPI(apiConfigOpts),
    rooms: new RoomsAPI(apiConfigOpts),
    events$: MessagingEvents(apiConfigOpts)
  };
}

/**
 * Constructor.
 *
 * @param {Function} apiConfigOpts.getToken
 * @param {string} apiConfigOpts.baseURL
 * @param {Observable<string,unknown>} apiConfigOpts.token$
 */
export function MessagingEvents(apiConfigOpts) {
  return apiConfigOpts.token$.pipe(switchMap(function (token) {
    return new Observable(function (observer) {
      if (!token) {
        return;
      }
      var claims = decodeJwt(token);
      if (claims.exp * 1000 < new Date().getTime()) {
        // TODO: We should see if we can log this anywhere else but the browser
        console.warn('Attempted to start SSE connection but the token is expired', token);
        observer.error(new Error('Attempted to start SSE connection but the token is expired'));
        return;
      }
      var url = apiConfigOpts.baseURL.startsWith('/') ? "".concat(window.location.origin).concat(apiConfigOpts.baseURL) : apiConfigOpts.baseURL;
      /* eslint no-undef: "error" */
      /* eslint-env browser */
      var eventSource = new EventSource("".concat(url, "/?__authorization=").concat(token));
      eventSource.onmessage = function (message) {
        var data = JSON.parse(message.data);
        observer.next(data);
      };
      eventSource.onerror = function (error) {
        console.error('Error in EventSource', error);
        observer.error(error);
      };
      return function () {
        eventSource.close();
      };
    });
  }), retryWhen(function (error$) {
    return error$.pipe(delay(1000));
  }), share());
}
export var MessageAPI = /*#__PURE__*/function (_APIClient) {
  function MessageAPI() {
    _classCallCheck(this, MessageAPI);
    return _callSuper(this, MessageAPI, arguments);
  }
  _inherits(MessageAPI, _APIClient);
  return _createClass(MessageAPI, [{
    key: "getConversationById",
    value: function getConversationById(id) {
      return this.client.get("/v3/conversations/".concat(id)).then(function (response) {
        return response.data;
      });
    }
  }, {
    key: "createMessage",
    value: function createMessage(message, conversationId) {
      return this.client.post("/v3/conversations/".concat(conversationId, "/messages/text"), {
        text: message
      }).then(function (response) {
        return response.data;
      });
    }
  }, {
    key: "deleteMessage",
    value: function deleteMessage(id) {
      return this.client.delete("/v3/messages/".concat(id)).then(function (response) {
        return response.data;
      });
    }
  }, {
    key: "createNote",
    value: function createNote(message, conversationId) {
      return this.client.post("/v3/conversations/".concat(conversationId, "/messages/note"), {
        text: message
      }).then(function (response) {
        return response.data;
      });
    }
  }, {
    key: "createTeamNote",
    value: function createTeamNote(message, conversationId, teamId) {
      return this.client.post("/v3/conversations/".concat(conversationId, "/messages/teamNote"), {
        text: message,
        team_id: teamId
      }).then(function (response) {
        return response.data;
      });
    }
  }, {
    key: "getMessages",
    value: function getMessages(id, params) {
      return this.client.get("/v3/conversations/".concat(id, "/messages"), {
        params: params
      }).then(function (response) {
        return response.data;
      });
    }
  }, {
    key: "isOnline",
    value: function isOnline(userIds) {
      return this.client.post("/v3/users/status/id-query", userIds).then(function (response) {
        return response.data;
      });
    }
  }, {
    key: "markRead",
    value: function markRead(messages, workspaceId) {
      return this.client.post("/v3/messages/read", {
        workspace_id: workspaceId,
        message_ids: messages.map(function (m) {
          return m.id;
        })
      }).then(function (response) {
        return response.data;
      });
    }
  }, {
    key: "getVideoToken",
    value: function getVideoToken(workspaceId) {
      return this.client.get("/v3/video/token", {
        params: {
          workspace_id: workspaceId
        }
      }).then(function (response) {
        return response.data;
      });
    }
  }, {
    key: "getParticipant",
    value: function getParticipant(params) {
      return this.client.get("/v3/conversations/".concat(params.conversation_id, "/participants/").concat(params.user_id), params).then(function (response) {
        return response.data;
      });
    }
  }, {
    key: "updateParticipant",
    value: function updateParticipant(params) {
      return this.client.patch("/v3/conversations/".concat(params.conversation_id, "/participants/").concat(params.user_id, "/update"), params).then(function (response) {
        return response.data;
      });
    }
  }]);
}(APIClient);

/**
 * Calls API
 *
 * @typedef {Object} CallParticipantDto
 * @property {string} user_id
 * @property {string} user_public_id
 * @property {string} status
 *
 *
 * @typedef {Object} CallDto
 * @property {string} id
 * @property {string} conversation_id
 * @property {string | null} date_completed
 * @property {string} call_status
 * @property {string} date_created
 * @property {string} date_modified
 * @property {CallParticipantDto[]} participants
 *
 */
export var CallAPI = /*#__PURE__*/function (_APIClient2) {
  function CallAPI() {
    _classCallCheck(this, CallAPI);
    return _callSuper(this, CallAPI, arguments);
  }
  _inherits(CallAPI, _APIClient2);
  return _createClass(CallAPI, [{
    key: "get",
    value: (
    /**
     * Fetches a call by id
     *
     * @param {string} id
     *
     * @returns {Promise<CallParticipantDto>}
     */
    function () {
      var _get = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(id) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              return _context.abrupt("return", this.client.get("/v3/calls/".concat(id)).then(function (response) {
                return response.data;
              }));
            case 1:
            case "end":
              return _context.stop();
          }
        }, _callee, this);
      }));
      function get(_x) {
        return _get.apply(this, arguments);
      }
      return get;
    }()
    /**
     * Starts or joins a with the given invitee identities
     *
     * @typedef {Object} CallParticipantIdentityPayload
     * @property {string} user_id
     * @property {string} user_public_id
     *
     * @typedef {Object} StartCallPayload
     * @property {string} conversation_id
     * @property {CallParticipantIdentityPayload[]} invitee_identities
     *
     * @typedef {Object} StartCallResponse
     * @property {CallDto} call
     * @property {*} call_connection_descriptor
     *
     *
     * @param {StartCallPayload} payload
     * @returns {Promise<StartCallResponse>}
     */
    )
  }, {
    key: "startOrJoin",
    value: (function () {
      var _startOrJoin = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2(payload) {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              return _context2.abrupt("return", this.client.post("/v3/calls", payload).then(function (response) {
                return response.data;
              }));
            case 1:
            case "end":
              return _context2.stop();
          }
        }, _callee2, this);
      }));
      function startOrJoin(_x2) {
        return _startOrJoin.apply(this, arguments);
      }
      return startOrJoin;
    }()
    /**
     * Joins a call
     *
     * @param {string} callId
     *
     * @typedef {Object} JoinCallReponse
     * @property {*} call_connection_descriptor
     *
     * @returns {Promise<JoinCallReponse>}
     */
    )
  }, {
    key: "join",
    value: (function () {
      var _join = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee3(callId) {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) switch (_context3.prev = _context3.next) {
            case 0:
              return _context3.abrupt("return", this.client.post("/v3/calls/".concat(callId, "/join")).then(function (response) {
                return response.data;
              }));
            case 1:
            case "end":
              return _context3.stop();
          }
        }, _callee3, this);
      }));
      function join(_x3) {
        return _join.apply(this, arguments);
      }
      return join;
    }()
    /**
     * Declines a call
     * @param {string} callId
     */
    )
  }, {
    key: "decline",
    value: (function () {
      var _decline = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee4(callId) {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) switch (_context4.prev = _context4.next) {
            case 0:
              _context4.next = 2;
              return this.client.post("/v3/calls/".concat(callId, "/decline"));
            case 2:
            case "end":
              return _context4.stop();
          }
        }, _callee4, this);
      }));
      function decline(_x4) {
        return _decline.apply(this, arguments);
      }
      return decline;
    }()
    /**
     * Declines a call
     * @param {string} callId
     */
    )
  }, {
    key: "hangup",
    value: (function () {
      var _hangup = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee5(callId) {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) switch (_context5.prev = _context5.next) {
            case 0:
              _context5.next = 2;
              return this.client.post("/v3/calls/".concat(callId, "/hangup"));
            case 2:
            case "end":
              return _context5.stop();
          }
        }, _callee5, this);
      }));
      function hangup(_x5) {
        return _hangup.apply(this, arguments);
      }
      return hangup;
    }())
  }]);
}(APIClient);

/**
 * RoomAPI
 *
 * @typedef {Object} WherebyRoomSourceDTO
 * @property {string} type
 * @property {string} room_source_id
 * @property {string} room_name
 *
 * @typedef {Object} LocalRoomSourceDTO
 * @property {string} type
 *
 * @typedef {WherebyRoomSourceDTO | LocalRoomSourceDTO} RoomSourceDTO
 *
 * @typedef {Object} RoomSessionParticipantDTO
 * @property {string} user_public_id
 * @property {string} status
 * @property {Date} date_connected
 * @property {Date} date_disconnected
 *
 * @typedef {Object} RoomSessionDTO
 * @property {string} id
 * @property {Date} date_started
 * @property {Date} date_modified
 * @property {string} status
 * @property {Array<RoomSessionParticipantDTO>} participants
 *
 * @typedef {Object} RoomDTO
 * @property {string} id
 * @property {Date} date_created
 * @property {Date} date_modified
 * @property {Array<string>} active_participants
 * @property {string} status
 * @property {RoomSourceDTO} room_source
 * @property {RoomSessionDTO} sessions
 *
 * @typedef WherebyRoomConnectionDescriptorDTO
 * @property {'Whereby'} type
 * @property {string} room_url
 *
 * @typedef LocalRoomConnectionDescriptorDTO
 * @property {'Local'} type
 *
 * @typedef {WherebyRoomConnectionDescriptorDTO | LocalRoomConnectionDescriptorDTO} RoomConnectionDescriptorDTO
 */
export var RoomsAPI = /*#__PURE__*/function (_APIClient3) {
  function RoomsAPI() {
    _classCallCheck(this, RoomsAPI);
    return _callSuper(this, RoomsAPI, arguments);
  }
  _inherits(RoomsAPI, _APIClient3);
  return _createClass(RoomsAPI, [{
    key: "get",
    value:
    /**
     *
     * @param {string} id
     * @returns {Promise<RoomDTO>}
     */
    function get(id) {
      return this.client.get("/v3/rooms/".concat(id)).then(this._data);
    }

    /**
     * Create a Room on-demand
     * @typedef {Object} CreateRoomPayload
     * @property {string} conversation_id
     * @property {string | null} cid
     *
     * @typedef {Object} RoomId
     * @property {string} id
     *
     * @param {CreateRoomPayload} payload
     * @returns {Promise<RoomId>}}
     */
  }, {
    key: "create",
    value: function create(payload) {
      return this.client.post("/v3/rooms", payload).then(this._data);
    }

    /**
     * Get the connection info
     * to join a room
     *
     * @param {string}id
     * @returns {RoomConnectionDescriptorDTO}
     */
  }, {
    key: "join",
    value: function join(id) {
      return this.client.get("/v3/rooms/".concat(id, "/join")).then(this._data);
    }
  }]);
}(APIClient);
//# sourceMappingURL=API.js.map
